// Nueva inspección (F3a): hacerla ahora o programarla con periodicidad (única, semanal, mensual, trimestral, semestral,
// anual), responsable (trabajador o cargo), sede (solo con 2 o más sedes activas; si no, la Matriz) y área opcional.
// Cada programación genera su inspección programada con su evento en el Calendario SSO (como los simulacros); al
// completarla o cancelarla se genera la siguiente.

import React, { useMemo, useState } from 'react';
import { borrarProgramacion, crearInspeccion, crearProgramacion, pausarProgramacion, ProgramacionEditable } from '../../lib/inspecciones/insp-datos';
import { plural } from '../../lib/plurales';
import { fechaEc, isoLocal, proximaFecha, validarProgramacion } from '../../lib/inspecciones/insp-logica';
import type { FilaInspeccion, FilaPlantilla, FilaProgramacion, Frecuencia, SedeMin, TrabajadorMin } from '../../lib/inspecciones/insp-tipos';
import { ETIQUETA_FRECUENCIA, FRECUENCIAS } from '../../lib/inspecciones/insp-tipos';
import SelectBuscable from '../../components/SelectBuscable';
import { Campo, ModalConfirmar } from '../pe/ComponentesPE';

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));

export interface PropsProgramar {
  companyId: string;
  plantillas: FilaPlantilla[];
  programaciones: FilaProgramacion[];
  inspecciones: FilaInspeccion[];
  sedes: SedeMin[];
  varias: boolean;
  trabajadores: TrabajadorMin[];
  puedeEditar: boolean;
  puedeBorrar?: boolean;
  usuarioId: string | null;
  onCreada: (i: FilaInspeccion, abrir: boolean) => void;
  onCambio: () => void;
  hoy: Date;
}

export default function ProgramarInsp(p: PropsProgramar) {
  const disponibles = useMemo(() => p.plantillas.filter(t => t.active && !t.is_library), [p.plantillas]);
  const matriz = p.sedes.find(s => s.is_main)?.id ?? null;
  const [modo, setModo] = useState<'ahora' | 'programar'>('ahora');
  const [f, setF] = useState<ProgramacionEditable>({
    template_id: '', site_id: matriz, area: '', frequency: 'mensual', start_date: isoLocal(p.hoy), end_date: null,
    responsible_worker_id: null, responsible_role: '', notes: '',
  });
  const [errores, setErrores] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [borrando, setBorrando] = useState<FilaProgramacion | null>(null);
  const [conPendientes, setConPendientes] = useState<boolean | null>(null);
  const pendientesDe = (s: FilaProgramacion | null) => (s ? p.inspecciones.filter(i => i.schedule_id === s.id && i.status === 'programada' && !i.signed_at).length : 0);

  const plantilla = disponibles.find(t => t.id === f.template_id);
  const nombrePlantilla = (id: string) => p.plantillas.find(t => t.id === id)?.name ?? 'Plantilla';

  async function ejecutar(accion: () => Promise<void>) {
    setOcupado(true); setError(''); setOk('');
    try { await accion(); } catch (e) { setError(msg(e)); } finally { setOcupado(false); }
  }

  const enviar = () => {
    const e = modo === 'ahora' ? (f.template_id ? [] : ['Elige la plantilla.']) : validarProgramacion(f);
    setErrores(e);
    if (e.length || !plantilla) return;
    ejecutar(async () => {
      if (modo === 'ahora') {
        const r = await crearInspeccion(p.companyId, { templateId: f.template_id, templateName: plantilla.name, siteId: p.varias ? f.site_id : null, area: f.area,
          fecha: isoLocal(p.hoy), responsableId: f.responsible_worker_id, responsableCargo: f.responsible_role, ahora: true }, p.usuarioId);
        p.onCreada(r.fila, true);
      } else {
        const r = await crearProgramacion(p.companyId, { ...f, site_id: p.varias ? f.site_id : null }, plantilla.name, p.usuarioId);
        p.onCreada(r.inspeccion, false);
        setOk(r.avisoCalendario ?? `Inspección programada para el ${fechaEc(r.inspeccion.scheduled_date)}. Quedó en el Calendario SSO con su recordatorio.`);
      }
    });
  };

  const siguienteDe = (s: FilaProgramacion) => {
    const pendiente = p.inspecciones.find(i => i.schedule_id === s.id && (i.status === 'programada' || i.status === 'en_curso'));
    if (pendiente) return `Próxima: ${fechaEc(pendiente.scheduled_date ?? pendiente.performed_date)}`;
    const ultima = p.inspecciones.filter(i => i.schedule_id === s.id).map(i => i.scheduled_date ?? i.performed_date ?? '').filter(Boolean).sort().pop() ?? null;
    const sig = proximaFecha(s, ultima);
    return sig ? `Siguiente: ${fechaEc(sig)}` : 'Sin más fechas';
  };

  return (
    <div className="pe2-columna">
      {p.puedeEditar && (
        <section className="pe2-card" aria-label="Nueva inspección">
          <h3>Nueva inspección</h3>
          <div className="pe2-vistas" role="tablist" aria-label="Cuándo">
            <button type="button" role="tab" aria-selected={modo === 'ahora'} className={modo === 'ahora' ? 'on' : ''} onClick={() => setModo('ahora')}>Hacerla ahora</button>
            <button type="button" role="tab" aria-selected={modo === 'programar'} className={modo === 'programar' ? 'on' : ''} onClick={() => setModo('programar')}>Programar</button>
          </div>
          <div className="pe2-grid">
            <Campo etiqueta="Plantilla" nota={plantilla?.frequency_source ? `Frecuencia sugerida: ${plantilla.default_frequency ? ETIQUETA_FRECUENCIA[plantilla.default_frequency] : '—'} (${plantilla.frequency_source})` : undefined}>
              <SelectBuscable value={f.template_id} disabled={ocupado} vacio="Elige una plantilla" onChange={v => {
                const t = disponibles.find(x => x.id === v);
                setF({ ...f, template_id: v, frequency: t?.default_frequency ?? f.frequency });
              }} opciones={disponibles.map(t => ({ value: t.id, label: `${t.name}${t.company_id ? '' : ' (base)'}` }))} />
            </Campo>
            {p.varias && (
              <Campo etiqueta="Sede">
                <SelectBuscable obligatorio value={f.site_id ?? ''} disabled={ocupado} onChange={v => setF({ ...f, site_id: v || null })}
                  opciones={p.sedes.filter(s => s.active).map(s => ({ value: s.id, label: s.name }))} />
              </Campo>
            )}
            <Campo etiqueta="Área (opcional)">
              <input type="text" value={f.area ?? ''} disabled={ocupado} onChange={e => setF({ ...f, area: e.target.value })} />
            </Campo>
            <Campo etiqueta="Responsable (trabajador)">
              <SelectBuscable value={f.responsible_worker_id ?? ''} disabled={ocupado} vacio="Sin trabajador" onChange={v => setF({ ...f, responsible_worker_id: v || null })}
                opciones={p.trabajadores.map(t => ({ value: t.id, label: t.full_name }))} />
            </Campo>
            <Campo etiqueta="Responsable (cargo)">
              <input type="text" value={f.responsible_role ?? ''} disabled={ocupado} onChange={e => setF({ ...f, responsible_role: e.target.value })} />
            </Campo>
            {modo === 'programar' && (
              <>
                <Campo etiqueta="Periodicidad">
                  <select value={f.frequency} disabled={ocupado} onChange={e => setF({ ...f, frequency: e.target.value as Frecuencia, end_date: e.target.value === 'unica' ? null : f.end_date })}>
                    {FRECUENCIAS.map(x => <option key={x} value={x}>{ETIQUETA_FRECUENCIA[x]}</option>)}
                  </select>
                </Campo>
                <Campo etiqueta="Fecha de la primera inspección">
                  <input type="date" value={f.start_date} disabled={ocupado} onChange={e => setF({ ...f, start_date: e.target.value })} />
                </Campo>
                {f.frequency !== 'unica' && (
                  <Campo etiqueta="Hasta (opcional)">
                    <input type="date" value={f.end_date ?? ''} disabled={ocupado} onChange={e => setF({ ...f, end_date: e.target.value || null })} />
                  </Campo>
                )}
              </>
            )}
          </div>
          {errores.length > 0 && <div className="pe2-error" role="alert">{errores.join(' ')}</div>}
          {error && <div className="pe2-error" role="alert">{error}</div>}
          {ok && <p className="pe2-ok" role="status">{ok}</p>}
          <div className="pe2-acciones">
            <button type="button" className="pe2-btn primario" onClick={enviar} disabled={ocupado}>{modo === 'ahora' ? 'Empezar inspección' : 'Programar inspección'}</button>
          </div>
        </section>
      )}
      <section className="pe2-card" aria-label="Programaciones">
        <h3>Programaciones</h3>
        {p.programaciones.length === 0 ? <p className="pe2-nota">No hay inspecciones programadas con periodicidad.</p> : (
          <div className="pe2-tarjetas">
            {p.programaciones.map(s => (
              <div key={s.id} className="pe2-tarjeta">
                <b>{nombrePlantilla(s.template_id)}</b>
                <span className="pe2-nota">{ETIQUETA_FRECUENCIA[s.frequency]} desde {fechaEc(s.start_date)}{s.end_date ? ` hasta ${fechaEc(s.end_date)}` : ''}
                  {p.varias ? ` · ${p.sedes.find(x => x.id === s.site_id)?.name ?? ''}` : ''}{s.area ? ` · ${s.area}` : ''}</span>
                <span className="pe2-nota">{s.active ? siguienteDe(s) : 'Pausada'}</span>
                {p.puedeEditar && (
                  <button type="button" className="pe2-btn" disabled={ocupado} onClick={() => ejecutar(async () => { await pausarProgramacion(s.id, !s.active); p.onCambio(); })}>
                    {s.active ? 'Pausar' : 'Reanudar'}
                  </button>
                )}
                {p.puedeBorrar && (
                  <button type="button" className="pe2-btn peligro" disabled={ocupado} aria-label={`Borrar la programación ${nombrePlantilla(s.template_id)}`}
                    onClick={() => { setBorrando(s); setConPendientes(null); }}>Borrar</button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
      <ModalConfirmar abierto={!!borrando} titulo="Borrar la programación" ocupado={ocupado} listo={pendientesDe(borrando) === 0 || conPendientes !== null}
        mensaje={`Se borra la programación de "${borrando ? nombrePlantilla(borrando.template_id) : ''}": no se generan más inspecciones. Las inspecciones en curso, completadas o canceladas se conservan.`}
        onCancelar={() => setBorrando(null)}
        onConfirmar={() => borrando && ejecutar(async () => {
          const n = await borrarProgramacion(borrando, p.inspecciones, conPendientes === true);
          setBorrando(null); p.onCambio();
          setOk(n ? `Programación borrada, con ${plural(n, 'inspección programada', 'inspecciones programadas')}.` : 'Programación borrada.');
        })}>
        {pendientesDe(borrando) > 0 && (
          <fieldset className="pe2-campo" style={{ border: 0, padding: 0, margin: '0 0 8px' }}>
            <legend>{`Generó ${plural(pendientesDe(borrando), 'inspección programada pendiente', 'inspecciones programadas pendientes')}. ¿También la${pendientesDe(borrando) === 1 ? '' : 's'} borras?`}</legend>
            <label><input type="radio" name="insp-borrar-pendientes" checked={conPendientes === true} onChange={() => setConPendientes(true)} disabled={ocupado} /> Sí, borrarlas con su evento del Calendario SSO</label>
            <label><input type="radio" name="insp-borrar-pendientes" checked={conPendientes === false} onChange={() => setConPendientes(false)} disabled={ocupado} /> No, dejarlas (quedan sin programación)</label>
          </fieldset>
        )}
      </ModalConfirmar>
    </div>
  );
}
